import { useCallback, useEffect, useMemo, useState } from 'react';

import type { DesiredSettingValue } from '@crossnokaye/typescript-sdk/models/components';
import { useToaster } from '@crossnokaye/ux-common';

import type { SaveProgressStats } from '@/components/settings/save-progress.utils';
import type { PreviewSaveStatus } from '@/components/settings/setting-preview.types';
import type { SettingChange } from '@/components/settings/settings-save.types';
import { useSettingsSave } from '@/components/settings/use-settings-save';
import type { FaultedDevice } from '@/features/equipment-browser/faulted-devices/faulted-devices.types';
import { useUpdateSettings } from '@/hooks/query-hooks/settings';
import { useCurrentFacility } from '@/hooks/use-current-facility';

import type { ModeValuesByDeviceId, ResetFaultedPayload } from '../reset-faulted.types';
import { buildResetFaultedChanges, mapSettingIdsToDeviceIds } from '../reset-faulted.utils';

interface UseResetFaultedSaveParams {
  /** Called with the devices to drop from the selection: reset successfully, or discarded. */
  onDeselect: (deviceIds: string[]) => void;
  /** Selected, resettable devices to reset. */
  selectedDevices: FaultedDevice[];
  targetModes: ModeValuesByDeviceId;
}

interface UseResetFaultedSaveResult {
  cancelPending: () => void;
  /**
   * Device changes taken when the dialog opened. Until Confirm, devices that stop
   * being resettable drop out; once the save starts the list stays fixed.
   */
  changes: SettingChange<ResetFaultedPayload>[];
  closeDialog: () => void;
  confirmSave: () => Promise<void>;
  discardChange: (changeId: string) => void;
  hasPendingSave: boolean;
  isDialogOpen: boolean;
  openDialog: () => void;
  progressStats: SaveProgressStats;
  saveComplete: boolean;
  saveProgressByChangeId: Record<string, PreviewSaveStatus>;
}

/**
 * Confirm-and-save workflow for the Reset Faulted pane. Each device is one
 * request through the shared settings write path, so the dialog shows progress
 * per device. Successful devices are deselected when the dialog closes; failed
 * ones stay selected for a retry. While the dialog is open, a device that stops
 * being resettable (it leaves the live selection) is dropped with a warning, so
 * Confirm never sends a reset the device's own Reset Fault button would refuse.
 */
export const useResetFaultedSave = ({
  onDeselect,
  selectedDevices,
  targetModes,
}: UseResetFaultedSaveParams): UseResetFaultedSaveResult => {
  const [openedChanges, setOpenedChanges] = useState<SettingChange<ResetFaultedPayload>[]>([]);
  const [confirmedChanges, setConfirmedChanges] = useState<
    SettingChange<ResetFaultedPayload>[] | null
  >(null);

  const { organizationId: orgId, agentId } = useCurrentFacility();
  const { mutateAsync: updateSettings } = useUpdateSettings({
    orgId,
    agentId,
    disableDefaultCallbacks: true,
  });
  const { addSuccessToast, addErrorToast, addWarningToast } = useToaster();

  // selectedDevices is the live selection: resettable devices with a resolved mode.
  const liveDeviceIds = useMemo(
    () => new Set(selectedDevices.map((device) => device.summary.referenceId)),
    [selectedDevices]
  );
  const liveChanges = useMemo(
    () => openedChanges.filter((change) => liveDeviceIds.has(change.changeId)),
    [liveDeviceIds, openedChanges]
  );
  const changes = confirmedChanges ?? liveChanges;

  // Before Confirm, tell the user about devices that dropped out of the reset.
  useEffect(() => {
    if (confirmedChanges || liveChanges.length === openedChanges.length) return;
    const dropped = openedChanges.filter((change) => !liveDeviceIds.has(change.changeId));
    addWarningToast(
      `${dropped.map((change) => change.label).join(', ')} can no longer be reset and ${
        dropped.length === 1 ? 'was' : 'were'
      } removed from this reset.`,
      10000
    );
    setOpenedChanges(liveChanges);
  }, [addWarningToast, confirmedChanges, liveChanges, liveDeviceIds, openedChanges]);

  const deviceIdBySettingId = useMemo(() => mapSettingIdsToDeviceIds(changes), [changes]);

  const handleSaveComplete = useCallback(
    ({ completed, failed }: { completed: string[]; failed: string[]; cancelled: string[] }) => {
      if (completed.length > 0) {
        onDeselect(completed);
      }
      if (failed.length > 0) {
        addErrorToast(
          `${failed.length} device(s) failed to reset. ${completed.length} succeeded.`,
          10000
        );
      } else if (completed.length > 0) {
        addSuccessToast(`Reset sent to ${completed.length} device(s).`, 5000);
      }
    },
    [addErrorToast, addSuccessToast, onDeselect]
  );

  const updateFn = useCallback(
    (items: ResetFaultedPayload[]) => updateSettings(items.flatMap((item) => item.settings)),
    [updateSettings]
  );

  const getItemId = useCallback((item: ResetFaultedPayload) => item.deviceId, []);

  // The mutation variables are the flattened settings of one device; map them
  // back to that device so progress is tracked per device.
  const getVariableItemIds = useCallback(
    (vars: unknown): string[] => {
      if (!Array.isArray(vars)) return [];
      const deviceIds = (vars as DesiredSettingValue[])
        .map((setting) => deviceIdBySettingId.get(setting.settingId))
        .filter((deviceId): deviceId is string => deviceId !== undefined);
      return [...new Set(deviceIds)];
    },
    [deviceIdBySettingId]
  );

  const settingsSave = useSettingsSave<ResetFaultedPayload>({
    changes,
    getItemId,
    getVariableItemIds,
    onSaveComplete: handleSaveComplete,
    updateFn,
  });

  const openDialog = useCallback(() => {
    setConfirmedChanges(null);
    setOpenedChanges(buildResetFaultedChanges(selectedDevices, targetModes));
    settingsSave.open();
  }, [selectedDevices, settingsSave, targetModes]);

  const closeDialog = useCallback(() => {
    settingsSave.close();
    setOpenedChanges([]);
    setConfirmedChanges(null);
  }, [settingsSave]);

  const confirmSave = useCallback(() => {
    // Fix the list for this save run; useSettingsSave sends the changes it was given.
    setConfirmedChanges(changes);
    return settingsSave.confirmSave();
  }, [changes, settingsSave]);

  const discardChange = useCallback(
    (changeId: string) => {
      if (settingsSave.hasPendingSave) return;
      setOpenedChanges((prev) => prev.filter((change) => change.changeId !== changeId));
      onDeselect([changeId]);
    },
    [onDeselect, settingsSave.hasPendingSave]
  );

  return {
    cancelPending: settingsSave.cancelPending,
    changes,
    closeDialog,
    confirmSave,
    discardChange,
    hasPendingSave: settingsSave.hasPendingSave,
    isDialogOpen: settingsSave.isOpen,
    openDialog,
    progressStats: settingsSave.progressStats,
    saveComplete: settingsSave.saveComplete,
    saveProgressByChangeId: settingsSave.saveProgressByChangeId,
  };
};
